import React, { useCallback, type FC } from 'react'
import { useIntl } from 'react-intl'
import { useAtom } from 'jotai'
import { useAtomCallback } from 'jotai/utils'
import {
  SidePanel,
  Flex,
  Title,
  TITLE_VARIANT_XL,
  FormElement,
  InputText,
  INPUT_TEXT_VARIANT_DANGER,
  Button,
  BUTTON_VARIANT_DANGER,
  Divider,
} from 'mailup-web-components'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { type IconProp } from '@fortawesome/fontawesome-svg-core'
import { faChevronsRight } from 'mailup-fontawesome-kit/classic/solid'
import { categoryFormState } from '../state/state'
import {
  closeCategoryFormCallback,
  submitCategoryFormCallback,
} from '../callbacks/form'
import { openModalDeleteCategoryCallback } from '../callbacks/modal'
import styled from '@emotion/styled'

const StyledDivider = styled(Divider)`
  height: 1px;
  background-color: var(--silver);
`

export const CategoryForm: FC = () => {
  const intl = useIntl()

  const [formState, setFormState] = useAtom(categoryFormState)
  const closeForm = useAtomCallback(closeCategoryFormCallback)
  const submitForm = useAtomCallback(submitCategoryFormCallback)
  const openDelete = useAtomCallback(
    useCallback(
      (get, set) => {
        formState.Id !== undefined &&
          openModalDeleteCategoryCallback(get, set, formState.Id)
      },
      [formState],
    ),
  )

  const handleChangeName = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setFormState((prev) => {
      return { ...prev, isDirty: true, Name: e.target.value }
    })
  }

  const showErrorFeedback = formState.Name === '' && formState.isDirty
  const requiredFeedback = intl.formatMessage({
    id: 'Load.MailupCommon.Required',
  })
  const formTitle = formState.title.startsWith('Load.')
    ? intl.formatMessage({ id: formState.title })
    : formState.title
  return (
    <SidePanel
      header={formTitle}
      show={formState.show}
      data-mu-test='category-form-panel'
    >
      <Flex flexDirection='column' gap='24px' padding='32px' minHeight='100%'>
        <Flex flexDirection='row'>
          <FontAwesomeIcon
            icon={faChevronsRight as IconProp}
            onClick={closeForm}
            fontSize='large'
            cursor='pointer'
            data-mu-test='category-form-close'
            data-tip={intl.formatMessage({
              id: 'Load.MailupCommon.PanelCloseTooltip',
            })}
          />
        </Flex>

        <Title variant={TITLE_VARIANT_XL} overflowWrap='anywhere'>
          {formTitle}
        </Title>

        <Flex flexDirection='column' gap='8px' flexGrow={1}>
          <FormElement
            labelFor='id-category-form-Name'
            data-mu-test='category-formElement-Name'
            required
            label={intl.formatMessage({ id: 'Load.MailupCommon.Name' })}
            feedback={showErrorFeedback ? requiredFeedback : undefined}
          >
            <InputText
              id='category-form-Name'
              data-mu-test='category-form-Name'
              name='Name'
              value={formState.Name}
              onChange={handleChangeName}
              variant={
                showErrorFeedback ? INPUT_TEXT_VARIANT_DANGER : undefined
              }
            />
          </FormElement>
        </Flex>

        <StyledDivider negativeMarginX={40} />
        <Flex flexDirection='row' gap='16px' justifyContent='end'>
          {formState.Id !== undefined && (
            <Button
              onClick={openDelete}
              data-mu-test='category-form-open-delete'
              kind={BUTTON_VARIANT_DANGER}
            >
              {intl.formatMessage({ id: 'Load.MailupCommon.Delete' })}
            </Button>
          )}
          <Button
            onClick={submitForm}
            disabled={formState.Name === ''}
            data-mu-test='category-form-save'
          >
            {intl.formatMessage({ id: 'Load.MailupCommon.Save' })}
          </Button>
        </Flex>
      </Flex>
    </SidePanel>
  )
}
